import type { DemandLaneCoverage } from '../types.ts';
import { formatDateRange, type CoverageSummary } from '../lib/demand.ts';

/**
 * The coverage line — "we looked here". It is what makes `no_signal` a statement about our search
 * rather than about the market, so every demand verdict is drawn with one and `no_signal` is never
 * drawn without (design-system.md, "Demand annotations"). All of it is counts and dates: mono.
 *
 * `scope` is printed, not implied: when an axis had no lane of its own the figures are the whole
 * run's, and a denominator must not pass for a more specific one than it is.
 */
export function DemandCoverage({
  summary,
  scope,
  searches = [],
}: {
  summary: CoverageSummary;
  scope: string;
  searches?: DemandLaneCoverage['searches'];
}): JSX.Element {
  const range = formatDateRange(summary.date_range_seen);
  return (
    <div className="demand-coverage">
      <span className="label">Where we looked</span>
      <p className="demand-coverage-line">
        {scope} · {summary.searches} {summary.searches === 1 ? 'search' : 'searches'} ·{' '}
        {summary.threads_fetched} {summary.threads_fetched === 1 ? 'thread' : 'threads'} fetched ·{' '}
        {summary.threads_kept} kept · {range ? `seen ${range}` : 'no dated threads seen'}
      </p>
      {searches.length > 0 && <SearchList searches={searches} />}
    </div>
  );
}

/** The queries themselves, behind a disclosure: the most checkable form of "we looked here". */
export function SearchList({ searches }: { searches: DemandLaneCoverage['searches'] }): JSX.Element {
  return (
    <details className="demand-searches">
      <summary>
        {searches.length} {searches.length === 1 ? 'query' : 'queries'}
      </summary>
      <ul>
        {searches.map((s, i) => (
          <li key={i}>
            {s.tool}
            {s.scope ? ` [${s.scope}]` : ''}: {s.query}
            {s.after ? ` · after ${s.after}` : ''} · {s.results} {s.results === 1 ? 'result' : 'results'}
          </li>
        ))}
      </ul>
    </details>
  );
}
